/**
 * AB#1754 — Calendar Settings section (C8 of Calendar v1 Epic AB#1746).
 *
 * Faithful port of mockup §I (CR-2026-05-20-v3) — see
 * `mockups/2026-05-20-calendar-v1-mockup.html` lines 1995-2232. Layout,
 * copy, toggle placement, and the purple `.note` banners are all
 * preserved verbatim from the design source so future copy / token
 * changes diff cleanly against the mockup.
 *
 * Two cards + one save bar:
 *
 *   Card 1 — "Calendar Time settings"
 *     · Header (left): title + subtitle
 *     · Header (right): two inline toggle rows — Overlap Time Slot, Back-dating
 *     · 3-col field grid: Week Start / Time Slot / Time Format
 *     · Purple settings-note explaining both toggles
 *
 *   Card 2 — "Calendar Color & label settings"
 *     · Header (no border bottom): title + subtitle
 *     · 5-col status table: Enable / Status / Color / Label (EN) / Label (AR)
 *     · 6 rows displayed: Booked, Arrived, Started, Completed, No-show, Cancelled
 *       — the legacy `Approved` enum value (StatusKey=2) is hidden from the
 *         UI per the mockup, but its CalendarStatusSettings row is preserved
 *         in the PUT body so the BE validator (which expects exactly 7
 *         entries — see AB#1754 SetCalendarSettingsCommandValidator) is
 *         satisfied. Historical Approved appointments continue to render
 *         using their stored color/label.
 *     · Purple settings-note explaining disable, contrast, label customization
 *
 *   Save bar — sticky at bottom
 *     · "Reset to defaults" (ghost) + "Save settings" (primary)
 *
 * Locked-status invariant (Booked + Completed cannot be disabled) is
 * derived client-side from `LOCKED_STATUS_KEYS` — the BE doesn't ship
 * an `isLocked` field on the wire; the server validator would also
 * reject the PUT if bypassed.
 *
 * Save flow: one atomic PUT containing the settings row + all 7 status
 * rows in a single transaction (BE has no per-status endpoint).
 *
 * Strings are intentionally hardcoded English — matches /my-business
 * convention per the Calendar v1 deferred-i18n batch (CR-2026-05-12-v2 /
 * AB#1713). AR translations land in the batch PBI.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
    useCalendarSettingsQuery,
    useUpdateCalendarSettingsMutation,
} from '../../../../hooks/useCalendarSettings';
import { useToast } from '../../../../contexts/ToastContext';
import { DEVANZA_DARK_BG, isReadableOnDarkBg, wcagContrastRatio } from '../../../../lib/colorContrast';
import {
    AppointmentStatusKey,
    LOCKED_STATUS_KEYS,
    type CalendarSettingsResponse,
    type CalendarStatusSettingsDto,
    type UpdateCalendarSettingsRequest,
} from '../../../../types/api-responses';
// AB#1901 (W-10) — use the central boundary parser so the BE→FE
// AppointmentStatusKey conversion follows the same convention as the
// rest of the enum-converter sweep. Drops the previous local `BE_NAME_TO_KEY`
// map in favour of the shared `parseAppointmentStatusKey` (which handles
// the "Pending" → Booked rename + every other status).
import { parseAppointmentStatusKey } from '../../../../lib/enumParsers';
import c from './CalendarSettingsSection.module.css';

interface SettingsFormState {
    weekStart: number;
    timeSlotMinutes: number;
    timeFormat: number; // 12 | 24
    overlapAllowed: boolean;
    backdatingAllowed: boolean;
}

const SLOT_GRANULARITY_OPTIONS = [5, 10, 15, 30, 60] as const;

/** Sun-start week ordering matches the BE DayOfWeek=0..6. */
const WEEK_START_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 6, label: 'Saturday' },
];

/**
 * Display order matches mockup §I — 6 rows, no `Approved`. Owners cannot
 * configure the legacy `Approved` (StatusKey=2) status from this page;
 * its row remains in the form state so the PUT body still ships all 7
 * entries the BE validator requires.
 */
/**
 * AB#1901 (W-10) — Local component state shape. The wire DTO carries
 * `statusKey: AppointmentStatusKey | string` because the BE serializes the
 * enum as a string name; after intake normalization (see useEffect below)
 * every row's statusKey is the integer AppointmentStatusKey, so all the
 * downstream Map<AppointmentStatusKey, ...>, Set<AppointmentStatusKey>,
 * and find-by-key logic stays sound.
 */
type NormalizedStatusRow = Omit<CalendarStatusSettingsDto, 'statusKey'> & {
    statusKey: AppointmentStatusKey;
};

const STATUS_DISPLAY_ORDER: AppointmentStatusKey[] = [
    AppointmentStatusKey.Booked,
    AppointmentStatusKey.Arrived,
    AppointmentStatusKey.Started,
    AppointmentStatusKey.Completed,
    AppointmentStatusKey.NoShow,
    AppointmentStatusKey.Cancelled,
];

function buildSettingsForm(snapshot: CalendarSettingsResponse): SettingsFormState {
    return {
        weekStart: snapshot.weekStart,
        timeSlotMinutes: snapshot.timeSlotMinutes,
        timeFormat: snapshot.timeFormat,
        overlapAllowed: snapshot.overlapAllowed,
        backdatingAllowed: snapshot.backdatingAllowed,
    };
}

function settingsEqual(a: SettingsFormState, b: SettingsFormState): boolean {
    return (
        a.weekStart === b.weekStart &&
        a.timeSlotMinutes === b.timeSlotMinutes &&
        a.timeFormat === b.timeFormat &&
        a.overlapAllowed === b.overlapAllowed &&
        a.backdatingAllowed === b.backdatingAllowed
    );
}

function statusEqual(a: NormalizedStatusRow, b: NormalizedStatusRow): boolean {
    return (
        a.statusKey === b.statusKey &&
        a.enabled === b.enabled &&
        a.colorHex.toLowerCase() === b.colorHex.toLowerCase() &&
        a.labelEn === b.labelEn &&
        a.labelAr === b.labelAr
    );
}

/** Normalize `#abc` → `#aabbcc` and uppercase for canonical compare. */
function normalizeHex(input: string): string {
    const trimmed = input.trim();
    if (!trimmed.startsWith('#')) return trimmed.toUpperCase();
    const body = trimmed.slice(1);
    if (body.length === 3) {
        return ('#' + body.split('').map((ch) => ch + ch).join('')).toUpperCase();
    }
    return trimmed.toUpperCase();
}

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;
// <input type="color"> only accepts a #rrggbb value, so its fallback cannot be a token (AB#2193).
// eslint-disable-next-line no-restricted-syntax
const COLOR_INPUT_FALLBACK = '#888888';

/**
 * AB#1901 (W-10) — Coerce a BE-wire `statusKey` (string or number) into the
 * FE numeric `AppointmentStatusKey`. Delegates to the central
 * `parseAppointmentStatusKey` which knows the BE→FE "Pending"→Booked rename
 * + every other status. Falls back to `Booked` so the row still renders if
 * the BE adds an unknown status before the FE knows about it.
 */
function normalizeStatusKey(input: number | string): AppointmentStatusKey {
    return parseAppointmentStatusKey(input) ?? AppointmentStatusKey.Booked;
}

const CalendarSettingsSection: React.FC = () => {
    const { showToast } = useToast();
    const { data, isLoading, isError, refetch } = useCalendarSettingsQuery();
    const mutation = useUpdateCalendarSettingsMutation();

    const [settingsForm, setSettingsForm] = useState<SettingsFormState | null>(null);
    const [statusRows, setStatusRows] = useState<NormalizedStatusRow[]>([]);

    // Seed local form whenever the server snapshot lands or refreshes.
    // CRITICAL: BE serializes the AppointmentStatus enum as a string
    // ("Pending", "Arrived", ...) via JsonStringEnumConverter. Normalize
    // each row's statusKey to the numeric AppointmentStatusKey so the
    // rest of the form (filtering, lookups, locked-status check) can
    // work uniformly with numbers.
    useEffect(() => {
        if (!data) return;
        setSettingsForm(buildSettingsForm(data));
        setStatusRows(
            data.statuses.map((r) => ({
                ...r,
                statusKey: normalizeStatusKey(r.statusKey),
            })),
        );
    }, [data]);

    // Rows actually rendered in the status table — filtered to mockup §I display set.
    const displayedRows = useMemo(() => {
        return STATUS_DISPLAY_ORDER
            .map((key) => statusRows.find((r) => r.statusKey === key))
            .filter((r): r is NormalizedStatusRow => !!r);
    }, [statusRows]);

    const isDirty = useMemo(() => {
        if (!data || !settingsForm) return false;
        if (!settingsEqual(settingsForm, buildSettingsForm(data))) return true;
        // Match the normalization applied to `statusRows` on intake — keys
        // here MUST be numeric for the Map.get(numeric) lookup to hit.
        const original = new Map(
            data.statuses.map((r) => {
                const normalized = {
                    ...r,
                    statusKey: normalizeStatusKey(r.statusKey),
                };
                return [normalized.statusKey, normalized];
            }),
        );
        return statusRows.some((r) => {
            const orig = original.get(r.statusKey);
            return !orig || !statusEqual(r, orig);
        });
    }, [data, settingsForm, statusRows]);

    const hexValidationErrors = useMemo(() => {
        const errs = new Map<AppointmentStatusKey, string>();
        for (const r of statusRows) {
            if (!HEX_REGEX.test(r.colorHex)) {
                errs.set(r.statusKey, 'Colour must be #RRGGBB');
            }
        }
        return errs;
    }, [statusRows]);

    const labelValidationErrors = useMemo(() => {
        const errs = new Map<AppointmentStatusKey, string>();
        for (const r of statusRows) {
            if (!r.labelEn.trim()) {
                errs.set(r.statusKey, 'EN label is required');
            } else if (r.labelEn.length > 64) {
                errs.set(r.statusKey, 'EN label too long (max 64)');
            } else if (r.labelAr.length > 64) {
                errs.set(r.statusKey, 'AR label too long (max 64)');
            }
        }
        return errs;
    }, [statusRows]);

    const contrastWarnings = useMemo(() => {
        const warns = new Map<AppointmentStatusKey, number>();
        for (const r of statusRows) {
            if (!HEX_REGEX.test(r.colorHex)) continue;
            if (!isReadableOnDarkBg(r.colorHex)) {
                warns.set(r.statusKey, wcagContrastRatio(r.colorHex, DEVANZA_DARK_BG));
            }
        }
        return warns;
    }, [statusRows]);

    const canSave =
        isDirty &&
        !mutation.isPending &&
        hexValidationErrors.size === 0 &&
        labelValidationErrors.size === 0;

    function updateSetting<K extends keyof SettingsFormState>(key: K, value: SettingsFormState[K]) {
        setSettingsForm((s) => (s ? { ...s, [key]: value } : s));
    }

    function updateStatusRow(statusKey: AppointmentStatusKey, patch: Partial<NormalizedStatusRow>) {
        setStatusRows((rows) =>
            rows.map((r) => (r.statusKey === statusKey ? { ...r, ...patch } : r)),
        );
    }

    function resetToDefaults() {
        if (!data) return;
        setSettingsForm(buildSettingsForm(data));
        setStatusRows(
            data.statuses.map((r) => ({
                ...r,
                statusKey: normalizeStatusKey(r.statusKey),
            })),
        );
    }

    async function save() {
        if (!settingsForm) return;
        // Ship ALL 7 rows (including the hidden Approved) so the BE validator
        // (exactly-7 invariant) is satisfied. Normalize colour hex to canonical
        // 6-digit uppercase and trim labels.
        const cleanStatuses = statusRows.map((r) => ({
            ...r,
            colorHex: normalizeHex(r.colorHex),
            labelEn: r.labelEn.trim(),
            labelAr: r.labelAr.trim(),
        }));
        const body: UpdateCalendarSettingsRequest = {
            ...settingsForm,
            statuses: cleanStatuses,
        };
        try {
            await mutation.mutateAsync(body);
            showToast('Calendar settings saved.', 'success');
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Save failed';
            showToast(`Couldn’t save: ${msg}`, 'error');
        }
    }

    // ── Intro block — rendered identically across loading / error / loaded
    //    so the page doesn't shift while the settings query hydrates.
    const introBlock = (
        <div className={c.intro}>
            <h1>Calendar settings</h1>
            <p>
                Easily adjust the time, color, and appointment settings on your calendar.
                <button type="button" className={c.introLink} onClick={() => refetch()}>
                    Learn more
                </button>
            </p>
        </div>
    );

    if (isLoading || !settingsForm) {
        return (
            <div className={c.page}>
                <div className={c.shell}>
                    {introBlock}
                    <div className={c.skeleton} aria-hidden="true" />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className={c.page}>
                <div className={c.shell}>
                    {introBlock}
                    <div className={c.errorState}>
                        <span>Couldn&apos;t load calendar settings.</span>
                        <button type="button" className={c.btnGhost} onClick={() => refetch()}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={c.page}>
            <div className={c.shell}>
                {introBlock}

                {/* ── Card 1: Calendar Time settings ─────────────────────── */}
                <div className={c.card}>
                    <div className={c.cardHead}>
                        <div>
                            <h2>Calendar Time settings</h2>
                            <p>Specify time-specific settings for your business.</p>
                        </div>
                        <div className={c.cardActions}>
                            <div className={c.cardActionsToggle}>
                                <span>Overlap Time Slot</span>
                                <button
                                    type="button"
                                    className={`${c.toggleBtn} ${settingsForm.overlapAllowed ? c.toggleBtnOn : ''}`}
                                    aria-pressed={settingsForm.overlapAllowed}
                                    aria-label="Allow overlapping appointments"
                                    onClick={() => updateSetting('overlapAllowed', !settingsForm.overlapAllowed)}
                                />
                            </div>
                            <div className={c.cardActionsToggle}>
                                <span>Back-dating</span>
                                <button
                                    type="button"
                                    className={`${c.toggleBtn} ${settingsForm.backdatingAllowed ? c.toggleBtnOn : ''}`}
                                    aria-pressed={settingsForm.backdatingAllowed}
                                    aria-label="Allow back-dated bookings"
                                    onClick={() => updateSetting('backdatingAllowed', !settingsForm.backdatingAllowed)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={c.fieldGrid}>
                        <div className={c.field}>
                            <label htmlFor="cs-week-start">Week Start From</label>
                            <select
                                id="cs-week-start"
                                className={c.select}
                                value={settingsForm.weekStart}
                                onChange={(e) => updateSetting('weekStart', Number(e.target.value))}
                            >
                                {WEEK_START_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className={c.field}>
                            <label htmlFor="cs-time-slot">Time Slot</label>
                            <select
                                id="cs-time-slot"
                                className={c.select}
                                value={settingsForm.timeSlotMinutes}
                                onChange={(e) => updateSetting('timeSlotMinutes', Number(e.target.value))}
                            >
                                {SLOT_GRANULARITY_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{m} Mins</option>
                                ))}
                            </select>
                        </div>

                        <div className={c.field}>
                            <label htmlFor="cs-time-format">Time Format</label>
                            <select
                                id="cs-time-format"
                                className={c.select}
                                value={settingsForm.timeFormat}
                                onChange={(e) => updateSetting('timeFormat', Number(e.target.value))}
                            >
                                <option value={12}>12 Hours</option>
                                <option value={24}>24 Hours</option>
                            </select>
                        </div>
                    </div>

                    <div className={c.note}>
                        <strong>Overlap Time Slot</strong> — when ON, the owner can manually book two appointments at the same time slot (e.g. for a customer who brings their daughter at the same time). When OFF, the booking is rejected with a conflict toast.<br />
                        <strong>Back-dating</strong> — when ON, the owner can record walk-ins after the fact (book in past time slots). When OFF, only current and future slots are bookable. Default OFF.
                    </div>
                </div>

                {/* ── Card 2: Calendar Color & label settings ────────────── */}
                <div className={c.card}>
                    <div className={`${c.cardHead} ${c.cardHeadNoBorder}`}>
                        <div>
                            <h2>Calendar Color &amp; label settings</h2>
                            <p>Customize the status colors and labels for your business. Bilingual (EN + AR).</p>
                        </div>
                    </div>

                    {/* Grid layout (NOT a semantic table) — see mockup §I
                        `.status-table`. ARIA roles preserve screen-reader
                        semantics on top of the flat grid structure. */}
                    <div className={c.statusTable} role="table" aria-label="Status palette">
                        <div className={c.statusHead} role="columnheader">Enable</div>
                        <div className={c.statusHead} role="columnheader">Status</div>
                        <div className={c.statusHead} role="columnheader">Color</div>
                        <div className={`${c.statusHead} ${c.colLabelEn}`} role="columnheader">Label (EN)</div>
                        <div className={`${c.statusHead} ${c.colLabelAr}`} role="columnheader">Label (AR)</div>

                        {displayedRows.map((row) => {
                            const isLocked = LOCKED_STATUS_KEYS.has(row.statusKey);
                            const hexErr = hexValidationErrors.get(row.statusKey);
                            const labelErr = labelValidationErrors.get(row.statusKey);
                            const contrastRatio = contrastWarnings.get(row.statusKey);
                            const rowDisabledClass = !row.enabled ? c.rowDisabled : '';
                            return (
                                <React.Fragment key={row.statusKey}>
                                    <div className={`${c.statusCell} ${rowDisabledClass}`} role="cell">
                                        <button
                                            type="button"
                                            className={`${c.toggleBtn} ${row.enabled ? c.toggleBtnOn : ''}`}
                                            aria-pressed={row.enabled}
                                            aria-label={`Toggle ${row.labelEn || `status ${row.statusKey}`}`}
                                            disabled={isLocked}
                                            title={isLocked ? 'Required status — cannot be disabled.' : undefined}
                                            onClick={() => updateStatusRow(row.statusKey, { enabled: !row.enabled })}
                                        />
                                    </div>
                                    <div className={`${c.statusCell} ${rowDisabledClass}`} role="cell">
                                        <span className={c.statusName}>
                                            <span
                                                className={c.statusDot}
                                                style={{ background: HEX_REGEX.test(row.colorHex) ? row.colorHex : 'transparent' }}
                                            />
                                            {row.labelEn || `Status ${row.statusKey}`}
                                        </span>
                                    </div>
                                    <div className={`${c.statusCell} ${rowDisabledClass}`} role="cell">
                                        <div className={c.colorField}>
                                            <input
                                                type="color"
                                                className={c.colorSwatch}
                                                value={HEX_REGEX.test(row.colorHex) ? row.colorHex : COLOR_INPUT_FALLBACK}
                                                onChange={(e) => updateStatusRow(row.statusKey, { colorHex: e.target.value })}
                                                aria-label={`${row.labelEn} colour picker`}
                                                disabled={!row.enabled}
                                            />
                                            <input
                                                type="text"
                                                className={c.hexInput}
                                                value={row.colorHex}
                                                onChange={(e) => updateStatusRow(row.statusKey, { colorHex: e.target.value })}
                                                aria-label={`${row.labelEn} colour hex`}
                                                maxLength={7}
                                                disabled={!row.enabled}
                                            />
                                        </div>
                                        {hexErr && <span className={c.fieldError}>{hexErr}</span>}
                                        {!hexErr && contrastRatio != null && (
                                            <span className={c.contrastWarn}>
                                                Contrast {contrastRatio.toFixed(2)}:1 — may be hard to read on the calendar grid.
                                            </span>
                                        )}
                                    </div>
                                    <div className={`${c.statusCell} ${c.cellLabelEn} ${rowDisabledClass}`} role="cell">
                                        <input
                                            type="text"
                                            className={c.textField}
                                            value={row.labelEn}
                                            onChange={(e) => updateStatusRow(row.statusKey, { labelEn: e.target.value })}
                                            maxLength={64}
                                            aria-label={`English label for ${row.labelEn || `status ${row.statusKey}`}`}
                                            disabled={!row.enabled}
                                        />
                                        {labelErr && <span className={c.fieldError}>{labelErr}</span>}
                                    </div>
                                    <div className={`${c.statusCell} ${c.cellLabelAr} ${rowDisabledClass}`} role="cell">
                                        <input
                                            type="text"
                                            className={`${c.textField} ${c.textFieldAr}`}
                                            value={row.labelAr}
                                            onChange={(e) => updateStatusRow(row.statusKey, { labelAr: e.target.value })}
                                            maxLength={64}
                                            dir="rtl"
                                            aria-label={`Arabic label for ${row.labelEn || `status ${row.statusKey}`}`}
                                            disabled={!row.enabled}
                                        />
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <div className={c.note}>
                        <strong>Disabling a status</strong> removes it from the state-machine transitions for new appointments. Historical appointments in that state are preserved and continue to render with their current color. Re-enabling restores the status for future bookings.<br />
                        <strong>Color contrast</strong> — the system validates each color against the dark-theme background for AA contrast. Colors that fail validation show an inline warning under the hex input.<br />
                        <strong>Label customization</strong> — labels are owner-editable in both EN and AR. Example: <em>Started</em> renamed to <em>In progress</em> / <em>قيد التنفيذ</em>.
                    </div>
                </div>

                {/* ── Save bar ───────────────────────────────────────────── */}
                <div className={c.saveBar}>
                    <button
                        type="button"
                        className={c.btnGhost}
                        disabled={!isDirty || mutation.isPending}
                        onClick={resetToDefaults}
                    >
                        Reset to defaults
                    </button>
                    <button
                        type="button"
                        className={c.btnPrimary}
                        disabled={!canSave}
                        onClick={save}
                    >
                        {mutation.isPending ? 'Saving…' : 'Save settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CalendarSettingsSection;
